import env from '@/infra/env'
import { defer, map, type Observable, type Subscription } from 'rxjs'
import { createAtom } from 'mobx'
import { share } from 'rxjs/operators'
import { LiveLDClient } from './LiveLDClient'
import { LocalLDClient } from './LocalLDClient'

/**
 * Possible flag value types.
 */
export type FlagValue = boolean | string | number

/**
 * A feature toggle function that checks a toggle when called.
 */
export type FeatureToggle<T extends FlagValue> = (() => T) & {
  /**
   * Emits when there's a change to the toggle's value.
   */
  changes$: Observable<void>
}

/**
 * Defines a toggle function which also allows observing changes.
 *
 * @param key
 * @param defaultValue
 */
export function defineToggle<T extends FlagValue>(
  key: string,
  defaultValue: T,
): FeatureToggle<T> {
  const checkToggle = () => featureToggleClient.variation(key, defaultValue)
  const changes$ = defer(() => featureToggleClient.flagChanges(key)).pipe(
    // Don't leak whatever the underlying client emits.
    map(() => undefined),
    share(),
  )

  // Create an Atom which lets us consume the toggle as an observable that
  // reacts to changes.
  let subscription: Subscription | null = null
  const atom = createAtom(
    `FeatureToggle(${key})`,
    () => {
      subscription = changes$.subscribe({
        next: () => {
          // We don't need the new value since we want the read
          // of the flag to be registered.
          atom.reportChanged()
        },
      })
    },
    () => {
      subscription?.unsubscribe()
      subscription = null
    },
  )

  /**
   * Check the feature toggle value and reports it as being observed.
   */
  function read() {
    atom.reportObserved()
    return checkToggle()
  }

  read.changes$ = changes$

  return read
}

/**
 * A user of feature toggles.
 */
export interface FeatureToggleUser {
  id: string
  email: string
  workspaceSlugs: string[]
}

/**
 * Feature toggle client contract.
 */
export interface FeatureToggleClient {
  /**
   * Checks the feature variation.
   *
   * @param key
   * @param defaultValue
   */
  variation<T extends FlagValue>(key: string, defaultValue: T): T

  /**
   * Subscribes to changes for a flag.
   * The flag value will not be emitted, as it is up to the consumer
   * if they wish to read the new value.
   */
  flagChanges(key: string): Observable<unknown>

  /**
   * Identifies the toggle client as the specified user.
   *
   * @param user
   */
  identify(user: FeatureToggleUser): Promise<void>
}

export const featureToggleClient: FeatureToggleClient =
  env.LAUNCHDARKLY_CLIENT_SIDE_ID
    ? LiveLDClient.create()
    : LocalLDClient.create()
